import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  ListSubheader,
  Divider,
  Tooltip,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ExpandLess,
  Remove as RemoveIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { NavLink } from "react-router-dom";
import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "@/hooks/useSession";
import { BASE_NAVIGATION } from "@/config/navigation";
import PropTypes from "prop-types";

const SidebarNavigation = ({ currentPath, collapsed }) => {
  const { t } = useTranslation();
  const { session } = useSession();
  const theme = useTheme();
  const [openStates, setOpenStates] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const toggleOpen = useCallback(
    (key) => setOpenStates((prev) => ({ ...prev, [key]: !prev[key] })),
    []
  );

  const clearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  const filterItem = useCallback(
    (item) => {
      if (
        item.allowedUserTypes &&
        !item.allowedUserTypes.includes(session?.user_type)
      ) {
        return null;
      }
      const copy = { ...item };
      if (item.children) {
        copy.children = item.children.map(filterItem).filter(Boolean);
        if (copy.children.length === 0) return null;
      }
      return copy;
    },
    [session?.user_type]
  );

  // Search functionality
  const searchItems = useCallback(
    (items, query) => {
      if (!query.trim()) return items;

      const lowerQuery = query.toLowerCase();
      const matchesQuery = (item) => {
        if (!item.titleKey) return false;
        const title = t(`sidebarNavigation.${item.titleKey}`).toLowerCase();
        return title.includes(lowerQuery);
      };

      const filterRecursive = (item) => {
        const matches = matchesQuery(item);
        const copy = { ...item };

        if (item.children) {
          copy.children = item.children
            .map(filterRecursive)
            .filter((child) => child !== null);

          // Include parent if any child matches or parent matches
          if (matches || copy.children.length > 0) {
            return copy;
          }
        } else if (matches) {
          return copy;
        }

        return null;
      };

      return items.map(filterRecursive).filter(Boolean);
    },
    [t]
  );

  const navigationItems = useMemo(() => {
    const filtered = BASE_NAVIGATION.map(filterItem).filter(Boolean);
    return searchItems(filtered, searchQuery);
  }, [filterItem, searchItems, searchQuery]);

  // Auto-expand parents of matching search results
  useMemo(() => {
    if (searchQuery.trim()) {
      const expandParents = (items, parentPath = "") => {
        items.forEach((item) => {
          const path = item.segment
            ? `/${[parentPath, item.segment].filter(Boolean).join("/")}`.replace(
                /\/+/g,
                "/"
              )
            : parentPath || "/";

          if (item.children) {
            setOpenStates((prev) => ({ ...prev, [path]: true }));
            expandParents(item.children, path);
          }
        });
      };
      expandParents(navigationItems);
    }
  }, [searchQuery, navigationItems]);

  const renderItem = (item, parentPath = "", depth = 0, index = 0) => {
    const path = item.segment
      ? `/${[parentPath, item.segment].filter(Boolean).join("/")}`.replace(
          /\/+/g,
          "/"
        )
      : parentPath || "/";

    const key =
      item.segment || `${item.kind || "item"}-${item.titleKey || index}`;

    const paddingLeft = collapsed ? 2 : 2 + depth * 1.5;
    const isActive =
      currentPath === path || currentPath === (item.defaultRoute || path);

    if (item.kind === "divider") {
      return (
        <Divider
          key={key}
          sx={{
            my: 1.5,
            mx: collapsed ? 2 : 1,
            borderColor: alpha(theme.palette.divider, 0.6),
          }}
        />
      );
    }

    if (item.kind === "header") {
      const headerText = t(`sidebarNavigation.${item.titleKey}`);
      return collapsed ? (
        <ListItem
          key={key}
          disablePadding
          sx={{
            justifyContent: "center",
            my: 1.5,
            opacity: 0.7,
          }}
        >
          <RemoveIcon
            sx={{
              fontSize: 20,
              color: theme.palette.primary.main,
              fontWeight: "bold",
            }}
          />
        </ListItem>
      ) : (
        <ListSubheader
          key={key}
          disableSticky
          sx={{
            mt: depth === 0 ? 2 : 1,
            mb: 0.5,
            px: 2,
            py: 1,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            borderRadius: 1,
            mx: 1,
            color: theme.palette.primary.main,
            fontWeight: 600,
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {headerText}
        </ListSubheader>
      );
    }

    if (item.children) {
      const isOpen = openStates[path] || false;
      const hasActiveChild = item.children.some((child) => {
        const childPath = child.segment
          ? `${path}/${child.segment}`.replace(/\/+/g, "/")
          : path;
        return (
          currentPath === childPath || currentPath.startsWith(`${childPath}/`)
        );
      });

      const buttonProps = collapsed
        ? {
            component: NavLink,
            to: item.defaultRoute || path,
            sx: (theme) => ({
              "&.active": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.contrastText,
                },
              },
            }),
          }
        : {
            onClick: () => toggleOpen(path),
          };

      const title = t(`sidebarNavigation.${item.titleKey}`);

      return (
        <Box key={key}>
          <ListItem disablePadding>
            <Tooltip title={collapsed ? title : ""} placement="right" arrow>
              <ListItemButton
                {...buttonProps}
                sx={{
                  pl: paddingLeft,
                  py: 1,
                  mx: collapsed ? 1 : 0.5,
                  borderRadius: collapsed ? 2 : 1,
                  justifyContent: collapsed ? "center" : "flex-start",
                  color: "text.primary",
                  transition: theme.transitions.create(["all"], {
                    duration: theme.transitions.duration.short,
                  }),
                  "& .MuiListItemIcon-root": {
                    color: "text.primary",
                    transition: "color 0.2s ease",
                  },
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    transform: collapsed ? "scale(1.05)" : "translateX(4px)",
                    "& .MuiListItemIcon-root": {
                      color: theme.palette.primary.main,
                    },
                  },
                  ...(isActive && {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    "& .MuiListItemIcon-root": {
                      color: theme.palette.primary.contrastText,
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                      transform: collapsed ? "scale(1.05)" : "translateX(4px)",
                    },
                  }),
                  ...(hasActiveChild &&
                    !isActive && {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                    }),
                }}
              >
                {item.icon && (
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 40,
                      justifyContent: "center",
                      mr: collapsed ? 0 : 1.5,
                      color: "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                )}
                {!collapsed && (
                  <>
                    <ListItemText
                      primary={title}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 500,
                        fontSize: "0.875rem",
                      }}
                    />
                    {item.children.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          ml: 1,
                          opacity: 0.7,
                          transition: "transform 0.2s ease",
                          transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                        }}
                      >
                        <ExpandLess fontSize="small" />
                      </Box>
                    )}
                  </>
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
          <Collapse
            in={isOpen && !collapsed}
            timeout={{
              enter: theme.transitions.duration.enteringScreen,
              exit: theme.transitions.duration.leavingScreen,
            }}
            unmountOnExit
          >
            <List component="div" disablePadding>
              {item.children.map((child, idx) =>
                renderItem(child, path, depth + 1, idx)
              )}
            </List>
          </Collapse>
        </Box>
      );
    }

    const title = t(`sidebarNavigation.${item.titleKey}`);

    return (
      <ListItem disablePadding key={key}>
        <Tooltip title={collapsed ? title : ""} placement="right" arrow>
          <ListItemButton
            component={NavLink}
            to={path}
            sx={(theme) => ({
              pl: paddingLeft,
              py: 1,
              mx: collapsed ? 1 : 0.5,
              borderRadius: collapsed ? 2 : 1,
              justifyContent: collapsed ? "center" : "flex-start",
              color: "text.primary",
              transition: theme.transitions.create(["all"], {
                duration: theme.transitions.duration.short,
              }),
              "& .MuiListItemIcon-root": {
                color: "text.primary",
                transition: "color 0.2s ease",
              },
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                transform: collapsed ? "scale(1.05)" : "translateX(4px)",
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
              "&.active": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.contrastText,
                },
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                  transform: collapsed ? "scale(1.05)" : "translateX(4px)",
                },
              },
            })}
          >
            {item.icon && (
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 40,
                  justifyContent: "center",
                  mr: collapsed ? 0 : 1.5,
                  color: "inherit",
                }}
              >
                {item.icon}
              </ListItemIcon>
            )}
            {!collapsed && (
              <ListItemText
                primary={title}
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: "0.875rem",
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        mt: collapsed ? 2 : 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search Section */}
      {!collapsed && (
        <Box
          sx={{
            px: 2, // Keep horizontal padding
            py: 1.5, // Reduced vertical padding from 2 to 1.5 for better alignment with breadcrumb
            borderBottom: 1,
            borderColor: alpha(theme.palette.divider, 0.5),
            bgcolor: alpha(theme.palette.grey[50], 0.3),
          }}
        >
          <TextField
            size="small"
            placeholder={t("sidebarNavigation.searchNavigation")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
                fontSize: "0.875rem",
                "&:hover fieldset": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
              },
              "& .MuiInputBase-input": {
                py: 1,
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: theme.palette.text.secondary }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Tooltip title={t("sidebarNavigation.clearSearch")}>
                      <ClearIcon
                        fontSize="small"
                        sx={{
                          cursor: "pointer",
                          color: theme.palette.text.secondary,
                          "&:hover": {
                            color: theme.palette.text.primary,
                          },
                        }}
                        onClick={clearSearch}
                      />
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />
          {searchQuery && (
            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {navigationItems.length} {t("sidebarNavigation.resultsFound")}
              </Typography>
              {navigationItems.length === 0 && (
                <Chip
                  label={t("sidebarNavigation.noResults")}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Navigation List */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <List disablePadding sx={{ py: 1 }}>
          {navigationItems.length === 0 && searchQuery ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {t("sidebarNavigation.noNavigationResults")}
              </Typography>
            </Box>
          ) : (
            navigationItems.map((item, idx) => renderItem(item, "", 0, idx))
          )}
        </List>
      </Box>
    </Box>
  );
};

SidebarNavigation.propTypes = {
  currentPath: PropTypes.string.isRequired,
  collapsed: PropTypes.bool.isRequired,
};

export default SidebarNavigation;
